import { useState } from "react";
import { CalendarDays } from "lucide-react";
import { Button } from "./ui/button";
import { Calendar } from "./ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";

// Small shadcn-Calendar-backed date picker that talks ISO strings
// (YYYY-MM-DD) so it can be a drop-in for the legacy <input type=date>
// usages in HolidaysDialog and ScheduleUnitDialog.
//
// Phase 132 — On phones (<sm = <640px) the rich popover is replaced
// with a native `<input type="date">`. Native pickers are pixel-
// perfect on every Android keyboard and iOS sheet, can't position
// themselves off-screen, and don't fight the parent dialog's
// fixed positioning. The rich popover stays the default on tablets
// and desktops where the Calendar grid gives a faster month/year
// scan.
//
// Props
//   value       ISO string ("YYYY-MM-DD") or empty string
//   onChange    (iso: string) => void
//   testid      optional data-testid passed to BOTH the mobile
//               native input AND the desktop popover trigger
//   placeholder shown when value is empty
const isoToDate = (iso) => {
  if (!iso) return undefined;
  const [y, m, d] = iso.split("-").map(Number);
  if (!y || !m || !d) return undefined;
  return new Date(y, m - 1, d);
};
const dateToIso = (d) => {
  if (!d) return "";
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};
const fmt = (iso) =>
  iso
    ? new Date(iso + "T00:00:00").toLocaleDateString(undefined, {
        weekday: "short",
        month: "short",
        day: "numeric",
        year: "numeric",
      })
    : "";

const DatePickerPopover = ({
  value,
  onChange,
  testid,
  placeholder = "Pick a date",
  className = "",
  disabled = false,
}) => {
  const [open, setOpen] = useState(false);
  return (
    <>
      {/* Mobile (<sm): native date input. Always reliable, can't
          position off-screen, gets the OS-native picker UI. */}
      <label
        className={`sm:hidden inline-flex items-center gap-2 px-3 py-2 rounded-md border border-input bg-background text-sm cursor-pointer hover:bg-accent ${
          disabled ? "opacity-50 cursor-not-allowed" : ""
        } ${className}`}
      >
        <CalendarDays className="h-4 w-4 text-[#4a6984] shrink-0" />
        <input
          type="date"
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          disabled={disabled}
          data-testid={testid ? `${testid}-mobile` : undefined}
          aria-label={placeholder}
          className="bg-transparent outline-none border-0 text-[#1c2d3d] flex-1 min-w-0 text-sm"
        />
      </label>

      {/* Desktop (≥sm): rich shadcn Calendar popover. */}
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            type="button"
            disabled={disabled}
            className={`hidden sm:inline-flex justify-start text-left font-normal ${className}`}
            data-testid={testid}
          >
            <CalendarDays className="h-4 w-4 mr-2 text-[#4a6984]" />
            {value ? fmt(value) : <span className="text-[#4a6984]/60">{placeholder}</span>}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="start">
          <Calendar
            mode="single"
            selected={isoToDate(value)}
            onSelect={(d) => {
              onChange(dateToIso(d));
              setOpen(false);
            }}
            initialFocus
          />
        </PopoverContent>
      </Popover>
    </>
  );
};

export default DatePickerPopover;
